import type { ReactNode } from 'react';
import { createBrowserRouter, RouterProvider, type RouteObject } from 'react-router-dom';

import { useSession } from './auth/AuthProvider';
import { RequireAuth } from './auth/RequireAuth';
import { AppShell } from './layout/AppShell';
import { AdminDrawer, AdminsPage } from './features/admins/AdminsPage';
import { AnalyticsPage } from './features/analytics/AnalyticsPage';
import { CoachDrawer } from './features/catalog/CoachDrawer';
import { CoachsPage } from './features/catalog/CoachsPage';
import { PromptEditor } from './features/catalog/PromptEditor';
import { PromptsLayout, PromptsListe } from './features/catalog/PromptsPage';
import { JournalPage } from './features/audit/JournalPage';
import { CreditsLayout, BulkView, LedgerView, RatesView, SummaryView } from './features/credits/CreditsPages';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { AccessKeyDrawer } from './features/keys/AccessKeyDrawer';
import { AccessKeysPage } from './features/keys/AccessKeysPage';
import { EventDrawer } from './features/events/EventDrawer';
import { EventsPage } from './features/events/EventsPage';
import { ConversationsPage } from './features/privacy/ConversationsPage';
import { AutorisationMcpPage } from './features/security/AutorisationMcpPage';
import { MonComptePage } from './features/security/MonComptePage';
import { PackDrawer, PacksPage, PlanDrawer, PlansPage } from './features/sales/OffresPages';
import { PaymentsLayout, PaymentsView, SubscribersPage, WebhooksView } from './features/sales/SuiviPages';
import { StudentDrawer } from './features/students/StudentDrawer';
import { StudentsPage } from './features/students/StudentsPage';
import { UnlockKeyDrawer, UnlockKeysPage } from './features/unlockKeys/UnlockKeysPage';
import { ALL_ENTRIES, type NavEntry } from './layout/navigation';
import { ForbiddenPage } from './pages/ForbiddenPage';
import { InvitationPage } from './pages/InvitationPage';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { UpcomingPage } from './pages/UpcomingPage';

/** Écran réservé au super admin : la page s'affiche, pas son contenu (l'API refuse de toute façon). */
function RoleGate({ entry, children }: { entry: NavEntry; children: ReactNode }) {
  const { user } = useSession();
  if (entry.superAdminOnly && !user?.isSuperAdmin) return <ForbiddenPage />;
  return <>{children}</>;
}

/** Écrans déjà construits (et leurs panneaux) ; les autres affichent leur page « à venir ». */
const ECRANS: Partial<Record<string, { element: ReactNode; children?: RouteObject[] }>> = {
  dashboard: { element: <DashboardPage /> },
  analytics: { element: <AnalyticsPage /> },
  admins: { element: <AdminsPage />, children: [{ path: ':id', element: <AdminDrawer /> }] },
  audit: { element: <JournalPage /> },
  conversations: { element: <ConversationsPage /> },
  coaches: { element: <CoachsPage />, children: [{ path: ':id', element: <CoachDrawer /> }] },
  prompts: {
    element: <PromptsLayout />,
    children: [
      { index: true, element: <PromptsListe /> },
      { path: ':id', element: <PromptEditor /> },
    ],
  },
  students: { element: <StudentsPage />, children: [{ path: ':id', element: <StudentDrawer /> }] },
  credits: {
    element: <CreditsLayout />,
    children: [
      { index: true, element: <LedgerView /> },
      { path: 'bilan', element: <SummaryView /> },
      { path: 'ajout-groupe', element: <BulkView /> },
      { path: 'bareme', element: <RatesView /> },
    ],
  },
  'access-keys': { element: <AccessKeysPage />, children: [{ path: ':id', element: <AccessKeyDrawer /> }] },
  'unlock-keys': { element: <UnlockKeysPage />, children: [{ path: ':id', element: <UnlockKeyDrawer /> }] },
  events: { element: <EventsPage />, children: [{ path: ':id', element: <EventDrawer /> }] },
  packs: { element: <PacksPage />, children: [{ path: ':id', element: <PackDrawer /> }] },
  plans: { element: <PlansPage />, children: [{ path: ':id', element: <PlanDrawer /> }] },
  subscribers: { element: <SubscribersPage /> },
  payments: {
    element: <PaymentsLayout />,
    children: [
      { index: true, element: <PaymentsView /> },
      { path: 'webhooks', element: <WebhooksView /> },
    ],
  },
};

export const routes: RouteObject[] = [
  { path: '/connexion', element: <LoginPage /> },
  { path: '/invitation', element: <InvitationPage /> },
  {
    path: '/mcp/autoriser',
    element: (
      <RequireAuth>
        <AutorisationMcpPage />
      </RequireAuth>
    ),
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      ...ALL_ENTRIES.map<RouteObject>((entry) => {
        const ecran = ECRANS[entry.id];
        const element = <RoleGate entry={entry}>{ecran?.element ?? <UpcomingPage entry={entry} />}</RoleGate>;
        if (entry.path === '/') return { index: true, element };
        return { path: entry.path.slice(1), element, ...(ecran?.children ? { children: ecran.children } : {}) };
      }),
      { path: 'mon-compte', element: <MonComptePage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

const router = createBrowserRouter(routes);

export function App() {
  return <RouterProvider router={router} />;
}
